import axios from "axios";
import { ContentAudience } from "../../types/contentAudience";
import { getAuthHeader } from "../../utils/authHeader";
import {
    InterviewReply, NicheOverview, NicheProfile, NicheScript, NicheTopic, SavedNicheProfile,
} from "./types";

const url = (path: string): string => `${process.env.REACT_APP_BACKEND_URL}/niche${path}`;
const auth = async (): Promise<{ headers: Record<string, string> }> => ({ headers: await getAuthHeader() });

const get = async <T>(path: string): Promise<T> => (await axios.get(url(path), await auth())).data;
const post = async <T>(path: string, body: unknown = {}): Promise<T> =>
    (await axios.post(url(path), body, await auth())).data;

export const getNicheOverview = (): Promise<NicheOverview> => get<NicheOverview>("");
export const saveNicheProfile = async (profile: NicheProfile): Promise<SavedNicheProfile> =>
    (await axios.put(url("/profile"), { profile }, await auth())).data;

export const startInterview = (): Promise<InterviewReply> => post<InterviewReply>("/interview/start");
export const answerInterview = (answer: string, turn?: number): Promise<InterviewReply> =>
    post<InterviewReply>("/interview/answer", turn === undefined ? { answer } : { answer, turn });
export const finishInterview = (): Promise<InterviewReply> => post<InterviewReply>("/interview/finish");
export const saveInterviewProfile = (profile: NicheProfile): Promise<SavedNicheProfile> =>
    post<SavedNicheProfile>("/interview/save", { profile });
export const discardInterview = (): Promise<void> => post<void>("/interview/discard");

export const getNicheTopics = (): Promise<{ topics: NicheTopic[]; scripts: NicheScript[] }> =>
    get<{ topics: NicheTopic[]; scripts: NicheScript[] }>("/topics");
// Residential is the api's default and is not sent, so an api without the voice field still accepts the request.
const sentVoice = (audience?: ContentAudience): ContentAudience | undefined =>
    audience === ContentAudience.Commercial ? audience : undefined;

export const addOwnTopic = (text: string, audience?: ContentAudience): Promise<NicheTopic> => {
    const voice = sentVoice(audience);
    return post<NicheTopic>("/topics/own", { text, ...(voice && { audience: voice }) });
};
export const searchLocal = (town: string, state: string, audience?: ContentAudience): Promise<{ topics: NicheTopic[] }> => {
    const voice = sentVoice(audience);
    return post<{ topics: NicheTopic[] }>("/local", { town, state, ...(voice && { audience: voice }) });
};

// Module state below is per user: admin impersonation switches users in the same
// tab without a reload (authSlice keeps the token in sessionStorage).
const sessionUser = (): string => {
    try {
        return sessionStorage.getItem("impersonationToken") ?? "self";
    } catch {
        return "self";
    }
};

// Each voice has its own set of ideas on the api, so the bookkeeping below is per user and voice.
const generationKey = (audience?: ContentAudience): string => `${sessionUser()}|${sentVoice(audience) ?? ""}`;
// Generating topics is charged. One generation at a time: a call while one is in
// flight (e.g. the step reopened mid first-use generation) shares that request.
const topicsInFlight = new Map<string, Promise<NicheTopic[]>>();
// A generation succeeded in this page session, so its topics are saved: the step's
// first-use path re-reads them rather than paying for another.
const topicsGenerated = new Set<string>();
export const refreshNicheTopics = (audience?: ContentAudience): Promise<NicheTopic[]> => {
    const key = generationKey(audience);
    const voice = sentVoice(audience);
    let request = topicsInFlight.get(key);
    if (!request) {
        request = post<NicheTopic[]>("/topics/refresh", { ...(voice && { audience: voice }) })
            .then((list) => {
                topicsGenerated.add(key);
                return list;
            })
            .finally(() => {
                topicsInFlight.delete(key);
            });
        topicsInFlight.set(key, request);
    }
    return request;
};
export const topicsGeneratedThisSession = (audience?: ContentAudience): boolean => topicsGenerated.has(generationKey(audience));

// Writing a script is charged too, and the api reuses one only once it is saved:
// an identical request while one is in flight (e.g. a re-tap after Back) shares it.
const scriptsInFlight = new Map<string, Promise<NicheScript>>();
export const getNicheScript = (topicId: string, rewrite = false, audience?: ContentAudience): Promise<NicheScript> => {
    const voice = sentVoice(audience);
    const key = `${sessionUser()}|${topicId}|${rewrite}|${voice ?? ""}`;
    let request = scriptsInFlight.get(key);
    if (!request) {
        request = post<NicheScript>("/script", { topicId, rewrite, ...(voice && { audience: voice }) }).finally(() => {
            scriptsInFlight.delete(key);
        });
        scriptsInFlight.set(key, request);
    }
    return request;
};

export const __resetNicheApiForTests = (): void => {
    topicsInFlight.clear();
    topicsGenerated.clear();
    scriptsInFlight.clear();
};

// The api's error bodies are { code, message, ...extra }.
const body = (error: unknown): Record<string, unknown> | undefined =>
    axios.isAxiosError(error) ? (error.response?.data as Record<string, unknown> | undefined) : undefined;

export const nicheErrorCode = (error: unknown): string | undefined => {
    const code = body(error)?.code;
    return typeof code === "string" ? code : undefined;
};

export const nicheErrorExtra = <T>(error: unknown, key: string): T | undefined => body(error)?.[key] as T | undefined;
